/* ==========================================================================
   <cart-drawer>
   Listens for ATC submits, mutates the cart, and re-renders its body from
   server-rendered section HTML (cart AJAX `sections` param). Rendering in
   Liquid — not client-side templating — keeps the {% form 'cart' %},
   express-checkout buttons, localized URLs, | money formatting and line
   discounts intact after every mutation.
   ========================================================================== */

function storefrontRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    || (window.theme && window.theme.routes && window.theme.routes.root)
    || '/';
}

function publishCartEvent(name, payload) {
  if (typeof window.CustomEvent !== 'function' || typeof document.dispatchEvent !== 'function') return;
  document.dispatchEvent(new window.CustomEvent('ev:commerce', {
    detail: { name, payload: payload || {} },
  }));
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-cart-body]');
    this.count = document.querySelector('[data-cart-count]');
    this.sectionId = this.dataset.sectionId || 'cart-drawer';
    this._chain = Promise.resolve(); // serialize mutations to avoid lost updates
    this._walletObserver = window.EssentialsVault?.cartCheckoutUi?.watch(this) || null;

    this._onProductAddClick = this._onProductAddClick || this._handleProductAddClick.bind(this);
    this._onProductSubmit = this._onProductSubmit || this._handleProductSubmit.bind(this);
    document.addEventListener('click', this._onProductAddClick);
    document.addEventListener('submit', this._onProductSubmit);

    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        this._enqueue(() => this._change(removeBtn.dataset.lineKey, 0));
        return;
      }
      const rmDiscount = e.target.closest('[data-discount-remove]');
      if (rmDiscount) {
        e.preventDefault();
        this._enqueue(() => this._removeDiscount());
        return;
      }
      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.lineKey;
        const delta = parseInt(qtyBtn.dataset.delta, 10);
        this._enqueue(() => this._changeBy(key, delta));
      }
    });

    this.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-discount-form]');
      if (!form) return;
      e.preventDefault();
      this._enqueue(() => this._applyDiscount(form));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onProductAddClick);
    document.removeEventListener('submit', this._onProductSubmit);
    if (this._walletObserver) this._walletObserver.disconnect();
  }

  _handleProductAddClick(event) {
    if (event.defaultPrevented) return;
    const submitter = event.target?.closest?.(
      'button[type="submit"], button:not([type]), input[type="submit"], input[type="image"]'
    );
    const form = submitter?.form || submitter?.closest?.('form');
    if (!form?.matches?.('form[action*="/cart/add"]')) return;

    // A native submit and the successful Ajax mutation each make Shopify emit
    // product_added_to_cart. Cancel the native default and let the confirmed
    // cart response remain the single standard ecommerce event.
    event.preventDefault();
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity?.();
      return;
    }
    this.handleAdd(event, form);
  }

  _handleProductSubmit(event) {
    if (event.defaultPrevented) return;
    const form = event.target?.closest?.('form[action*="/cart/add"]');
    if (!form) return;
    this.handleAdd(event, form);
  }

  // Chain cart mutations so a rapid double-click can't read-modify-write a
  // stale quantity (each task runs after the previous one has re-rendered).
  _enqueue(task) {
    this._chain = this._chain.then(task).catch((err) => console.error(err));
    return this._chain;
  }

  async handleAdd(e, form) {
    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const stickyBtn = form.closest('variant-picker')?.querySelector('[data-sticky-atc-btn]');
    const buttonStates = [submitBtn, stickyBtn].filter(Boolean).map((button) => ({
      button,
      disabled: button.disabled,
      label: button.querySelector('[data-atc-label]'),
    }));
    // Ignore repeat submits while a request is in flight — otherwise a fast
    // double-click adds two of a 1-of-1 resale piece before the first resolves.
    if (submitBtn && submitBtn.getAttribute('aria-busy') === 'true') return;
    this._clearAddError(form);
    const fd = new FormData(form);
    fd.append('sections', this.sectionId);
    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const cartPayload = {
      variant_id: variantInput?.value,
      quantity: parseInt(quantityInput?.value || '1', 10),
      source: 'product_form',
    };
    publishCartEvent('add_to_cart_attempt', cartPayload);
    buttonStates.forEach((state) => {
      state.idleLabel = state.label?.textContent || '';
      state.button.disabled = true;
      state.button.setAttribute('aria-busy', 'true');
      if (state.label && state.button.dataset.loadingLabel) {
        state.label.textContent = state.button.dataset.loadingLabel;
      }
    });
    try {
      const res = await fetch(`${storefrontRoot()}cart/add.js`, {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/javascript' },
      });
      const data = await res.json();
      // 422 (e.g. only 1 in stock, already in cart) returns a localized description.
      if (!res.ok) throw new Error((data && data.description) || 'Add to cart failed');
      publishCartEvent('add_to_cart_success', Object.assign({}, cartPayload, {
        product_id: data && data.product_id,
        variant_id: (data && (data.variant_id || data.id)) || cartPayload.variant_id,
      }));
      await this._render(data.sections);
      this._refreshSibling('cart-page');
      this.open();
    } catch (err) {
      // Surface the failure instead of failing silently — the shopper clicked
      // "Add to bag" and nothing visible happened before this.
      this._showAddError(form, err.message);
    } finally {
      buttonStates.forEach((state) => {
        state.button.disabled = state.disabled;
        state.button.removeAttribute('aria-busy');
        if (state.label) state.label.textContent = state.idleLabel;
      });
    }
  }

  _showAddError(form, msg) {
    let el = form.querySelector('[data-atc-error]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-atc-error', '');
      el.setAttribute('role', 'alert');
      el.className = 'atc-error';
      const btn = form.querySelector('[type="submit"]');
      if (btn && btn.parentNode) btn.parentNode.insertBefore(el, btn.nextSibling);
      else form.appendChild(el);
    }
    el.textContent = msg || 'Add to cart failed';
    el.hidden = false;
  }

  _clearAddError(form) {
    const el = form.querySelector('[data-atc-error]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  async _changeBy(key, delta) {
    if (!key) return;
    const cart = await (await fetch(`${storefrontRoot()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const line = cart.items.find((i) => i.key === key);
    if (!line) return;
    await this._change(key, Math.max(0, line.quantity + delta));
  }

  async _change(key, quantity) {
    if (!key) return;
    const res = await fetch(`${storefrontRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: key, quantity, sections: this.sectionId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error('Cart update failed');
    await this._render(data.sections);
    this._refreshSibling('cart-page');
  }

  async _render(sections) {
    let html = sections && sections[this.sectionId];
    if (!html) {
      // Fallback: Section Rendering API (used if the mutation response lacked
      // the section, e.g. a follow-up refresh).
      const root = storefrontRoot();
      html = await (await fetch(`${root}?section_id=${this.sectionId}`)).text();
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newBody = doc.querySelector('[data-cart-body]');
    if (newBody && this.body) this.body.innerHTML = newBody.innerHTML;
    this._syncCount();
    window.EssentialsVault?.cartCheckoutUi?.sync(this);
    this._reinitPaymentButtons();
  }

  // Shopify wires dynamic checkout buttons (Shop Pay / Apple Pay / …) on load;
  // after we swap the drawer's innerHTML the fresh markup needs re-initialising,
  // or the express-checkout wallets go inert after the first add / qty change.
  _reinitPaymentButtons() {
    try {
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        window.Shopify.PaymentButton.init();
      }
    } catch (err) {
      /* non-fatal */
    }
  }

  _syncCount() {
    if (!this.count) return;
    const src = this.querySelector('[data-cart-count-source]');
    const n = src ? parseInt(src.textContent, 10) : NaN;
    if (Number.isNaN(n)) return;
    this.count.textContent = n;
    this.count.toggleAttribute('hidden', n === 0);
  }

  async _applyDiscount(form) {
    const helper = window.EssentialsVault && window.EssentialsVault.cartDiscount;
    if (!helper) return;
    const box = form.closest('[data-cart-discount]');
    const input = form.querySelector('[data-discount-input]');
    const code = input ? input.value.trim() : '';
    if (!code) return;
    if (box) box.setAttribute('data-busy', 'true');
    try {
      const { applicable } = await helper.apply(code);
      if (!applicable) {
        await helper.remove(); // drop the rejected code so it can't reach checkout
        this._showDiscountError(box);
        return;
      }
      await this._render();
      this._refreshSibling('cart-page'); // the full cart page shares this cart — keep it in sync
    } catch (err) {
      this._showDiscountError(box);
    } finally {
      if (box) box.removeAttribute('data-busy');
    }
  }

  async _removeDiscount() {
    const helper = window.EssentialsVault && window.EssentialsVault.cartDiscount;
    if (!helper) return;
    await helper.remove();
    await this._render();
    this._refreshSibling('cart-page');
  }

  _showDiscountError(box) {
    const el = box && box.querySelector('[data-discount-error]');
    if (!el) return;
    if (el.dataset.msg) el.textContent = el.dataset.msg;
    el.hidden = false;
  }

  // A discount changes the whole cart, so the other cart UI (the full cart page,
  // present on /cart alongside this drawer) must re-render too or it goes stale.
  _refreshSibling(selector) {
    const el = document.querySelector(selector);
    if (el && typeof el.refresh === 'function') el.refresh();
  }

  // Public: re-fetch and re-render the drawer body from the server. Used by
  // callers that mutate the cart outside this element (quick-add, buy-the-outfit
  // modal) and then want the drawer to reflect the new state before opening.
  async refresh() {
    return this._enqueue(() => this._render());
  }

  open() {
    const wasOpen = this.getAttribute('data-open') === 'true';
    this.setAttribute('data-open', 'true');
    document.querySelector('[data-cart-overlay]')?.setAttribute('data-visible', 'true');
    window.theme.lockScroll('cart');
    if (!wasOpen) publishCartEvent('cart_opened', { source: 'cart_drawer' });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
